import { MNEMONICS } from 'constants/mnemonics';
import {
  KICK_DETECTION_DATA_MANAGEMENT_MNEMONICS,
  KICK_PREDICTION_DATA_MANAGEMENT_MNEMONICS,
  STUCK_PREDICTION_DATA_MANAGEMENT_MNEMONICS,
} from 'constants/mnemonicsMapping';
import { getActiveTriptankMnemonicFromLiveData } from 'pages/DataManagementDataQuality/mnemonicsHelper';
import { MnemonicsMappingAndLiveStatusDTO } from 'types/mnemonicsMapping';

export interface MnemonicsRatioData {
  length: {
    kickDetection: number;
    kickPrediction: number;
    stuckPrediction: number;
  };
  live: {
    kickDetection: number;
    kickPrediction: number;
    stuckPrediction: number;
  };
}

const getLiveMnemonicNumberForScope = (
  isFlowInBstNA: boolean,
  isFlowOutAVNA: boolean,
  isFlowOutPCNA: boolean,
  scopeMnemonics: string[],
  mnemonicsMapping?: MnemonicsMappingAndLiveStatusDTO,
) => {
  const oneHourAgo = new Date();
  oneHourAgo.setHours(oneHourAgo.getHours() - 1);

  return scopeMnemonics.reduce((liveMapping, mnemonic) => {
    if (mnemonic === MNEMONICS.FLOWIN_BST && isFlowInBstNA) return liveMapping;
    if (mnemonic === MNEMONICS.FLOWOUTAV && isFlowOutAVNA) return liveMapping;
    if (mnemonic === MNEMONICS.FLOWOUTPC && isFlowOutPCNA) return liveMapping;

    if (mnemonic === MNEMONICS.TTVOL01 && mnemonicsMapping) {
      const tripTankMnemonic = getActiveTriptankMnemonicFromLiveData(mnemonicsMapping.liveData);
      if (!tripTankMnemonic) {
        return liveMapping;
      }
    }

    if (mnemonic === MNEMONICS.INCC) {
      return liveMapping + (mnemonicsMapping?.depthData[mnemonic]?.live ? 1 : 0);
    }

    return (
      liveMapping +
      (mnemonicsMapping?.liveData[mnemonic] &&
      new Date(mnemonicsMapping.liveData[mnemonic]) > oneHourAgo
        ? 1
        : 0)
    );
  }, 0);
};

const getOneMappingIndex = (mnemonicsLength: number, mnemonicsLive: number) => {
  if (mnemonicsLength === 0) return 0;
  const missingMnemonicsCount = mnemonicsLength - mnemonicsLive;
  if (missingMnemonicsCount === 0) return 1;
  if (missingMnemonicsCount === 1) return 0.8;
  if (missingMnemonicsCount === 2) return 0.7;
  if (missingMnemonicsCount === 3) return 0.5;
  if (missingMnemonicsCount === 4) return 0.3;
  if (missingMnemonicsCount === 5) return 0.2;

  return 0;
};

export const computeGlobalMappingIndex = (mnemonicsRatioData: MnemonicsRatioData) => {
  const kickDetectionMappingIndex = getOneMappingIndex(
    mnemonicsRatioData.length.kickDetection,
    mnemonicsRatioData.live.kickDetection,
  );
  const kickPredictionMappingIndex = getOneMappingIndex(
    mnemonicsRatioData.length.kickPrediction,
    mnemonicsRatioData.live.kickPrediction,
  );
  const stuckPredictionMappingIndex = getOneMappingIndex(
    mnemonicsRatioData.length.stuckPrediction,
    mnemonicsRatioData.live.stuckPrediction,
  );

  return (
    Math.round(
      (100 *
        (kickDetectionMappingIndex + kickPredictionMappingIndex + stuckPredictionMappingIndex)) /
        3,
    ) / 100
  );
};

export const getAllMnemonicsRatioData = (
  isFlowInBstNA: boolean,
  isFlowOutAVNA: boolean,
  isFlowOutPCNA: boolean,
  mnemonicsMapping?: MnemonicsMappingAndLiveStatusDTO,
) => {
  const kickDetectionNumberOfNAMnemonics = [isFlowInBstNA, isFlowOutAVNA, isFlowOutPCNA].reduce(
    (sum, isMnemonicNa) => sum + (isMnemonicNa ? 1 : 0),
    0,
  );

  return {
    length: {
      kickDetection:
        KICK_DETECTION_DATA_MANAGEMENT_MNEMONICS.length - kickDetectionNumberOfNAMnemonics,
      kickPrediction: KICK_PREDICTION_DATA_MANAGEMENT_MNEMONICS.length - (isFlowInBstNA ? 1 : 0),
      stuckPrediction: STUCK_PREDICTION_DATA_MANAGEMENT_MNEMONICS.length,
    },
    live: {
      kickDetection: getLiveMnemonicNumberForScope(
        isFlowInBstNA,
        isFlowOutAVNA,
        isFlowOutPCNA,
        KICK_DETECTION_DATA_MANAGEMENT_MNEMONICS,
        mnemonicsMapping,
      ),
      kickPrediction: getLiveMnemonicNumberForScope(
        isFlowInBstNA,
        isFlowOutAVNA,
        isFlowOutPCNA,
        KICK_PREDICTION_DATA_MANAGEMENT_MNEMONICS,
        mnemonicsMapping,
      ),
      stuckPrediction: getLiveMnemonicNumberForScope(
        isFlowInBstNA,
        isFlowOutAVNA,
        isFlowOutPCNA,
        STUCK_PREDICTION_DATA_MANAGEMENT_MNEMONICS,
        mnemonicsMapping,
      ),
    },
  };
};
